/**
 * Sub-ledger to General Ledger reconciliation and reconciliation certification
 * (FSD 2.2.8 FIN-F09-V02, AC02, "Certify Reconciliation"; FT8-B14).
 *
 * Every call is the generated `@erpx/api` function:
 *
 *  - `closeReconciliation`            GET  /close-reconciliation
 *  - `closeRunsCertifyReconciliation` POST /close-runs/:id/certify-reconciliation
 *  - `attachmentsUpload` (through the shared attachments wrapper) for the
 *    evidence file the certification rests on.
 *
 * **The certifier rule is the server's.** `certifierVerdict` restates
 * `refuseSelfCertifiedReconciliation` (server `close/certify_recon.go`) from the
 * run's own sign-offs, and only so far as the run detail lets the browser know
 * it for certain: it answers "excluded" only where the server is guaranteed to
 * refuse, and "unknown" — keep the action, let the server decide — wherever the
 * server reads something the browser cannot see (who opened or closed the bank
 * reconciliation sessions, who posted the period's last fixed-asset journal). A
 * hidden action the server would have allowed is as wrong as a shown one it
 * refuses; this errs only towards showing.
 *
 * Money is the server's string throughout and is never parsed here.
 */

import {
  closeReconciliation,
  closeRunsCertifyReconciliation,
  type CloseCertificationDTO,
  type CloseSubledgerReconAccountDTO,
  type CloseSubledgerReconDTO,
  type CloseSubledgerReconItemDTO,
} from '@erpx/api'

import { api } from '@/api/client'
import { uploadAttachment, type AttachmentDTO } from '@/components/attachments/api'
import {
  EDITABLE_RUN_STATUSES,
  getRun,
  listRuns,
  type CloseRunDetailDTO,
  type CloseSignoffDTO,
  type CloseTaskDTO,
} from '@/modules/close/api'

export type {
  CloseCertificationDTO,
  CloseSubledgerReconAccountDTO,
  CloseSubledgerReconDTO,
  CloseSubledgerReconItemDTO,
}

/** The permissions the reconciliation surfaces declare (`internal/close/permissions.go`). */
export const RECON_PERMISSIONS = {
  /** GET /close-reconciliation. */
  read: 'close.reconciliation.read',
  /** POST /close-runs/:id/certify-reconciliation — step-up MFA server-side. */
  certify: 'close.run.certify',
  /** Reading the period's close run, to know who prepared what. */
  runRead: 'close.run.read',
  /** POST /attachments — the evidence file. */
  attachmentUpload: 'attachment.upload',
} as const

/** `csg_recon_type_check`, in the server's presentation order. */
export type CloseReconType = 'bank' | 'ap' | 'ar' | 'inventory' | 'fixed_assets'

export const RECON_TYPES: readonly CloseReconType[] = ['bank', 'ap', 'ar', 'inventory', 'fixed_assets']

/** `CloseReconType.ProviderKey()`: the readiness provider each certification attests. */
export const RECON_PROVIDER_KEYS: Readonly<Record<CloseReconType, string>> = {
  bank: 'bankrec.sessions_closed',
  ap: 'ap.control_reconciles',
  ar: 'ar.control_reconciles',
  inventory: 'inventory.valuation_reconciles',
  fixed_assets: 'assets.control_reconciles',
}

/**
 * The types whose preparer the server reads ONLY from the run's task
 * sign-offs. Bank adds the reconciliation sessions' openers and closers, fixed
 * assets the period's last asset poster — neither is on the run detail.
 */
const TASK_BOUND_ONLY: readonly CloseReconType[] = ['ap', 'ar', 'inventory']

export function isReconType(value: string): value is CloseReconType {
  return (RECON_TYPES as readonly string[]).includes(value)
}

export const RECON_KEYS = {
  report: (companyId: number, periodId: number) => ['close', 'reconciliation', companyId, periodId] as const,
  periodRun: (companyId: number, periodId: number) =>
    ['close', 'reconciliation', 'run', companyId, periodId] as const,
} as const

/* ------------------------------------------------------------------ reads */

export function subledgerReconciliation(companyId: number, periodId: number): Promise<CloseSubledgerReconDTO> {
  return closeReconciliation(api(), { companyId, periodId })
}

/**
 * The close run a certification for this period is recorded on: the period's
 * run that still accepts writes. A certified, closed or abandoned run takes no
 * new certification (the server answers 409/422), so it is not offered.
 * `null` when the period has no such run — certification then waits for one.
 */
export async function findCertifiableRun(companyId: number, periodId: number): Promise<CloseRunDetailDTO | null> {
  const page = await listRuns({ companyId, fiscalPeriodId: periodId }, { limit: 50 })
  const open = page.items.find((run) => EDITABLE_RUN_STATUSES.includes(run.status))
  return open === undefined ? null : getRun(open.id)
}

/* ---------------------------------------------------------- certifier rule */

/**
 * - `allowed`: the server's preparer rule passes for this person.
 * - `preparer`: they signed off a task bound to this reconciliation.
 * - `sole_preparer`: nothing names a preparer for the type and they signed
 *   every task on the run.
 * - `no_preparer`: nothing on the run carries a sign-off, so there is nobody
 *   to segregate from and the server refuses everyone.
 * - `unknown`: the server reads a preparer the browser cannot see.
 */
export type CertifierVerdict = 'allowed' | 'preparer' | 'sole_preparer' | 'no_preparer' | 'unknown'

export function certifierVerdict(
  run: { signoffs: readonly CloseSignoffDTO[]; tasks: readonly Pick<CloseTaskDTO, 'id' | 'readinessKey'>[] },
  reconType: CloseReconType,
  userId: number | null,
): CertifierVerdict {
  if (userId === null) return 'unknown'
  const providerKey = RECON_PROVIDER_KEYS[reconType]
  const keyByTask = new Map(run.tasks.map((task) => [task.id, task.readinessKey]))
  const taskSignoffs = run.signoffs.filter((signoff) => signoff.kind === 'task')
  const bound = new Set(
    taskSignoffs
      .filter(
        (signoff) =>
          signoff.taskId !== undefined && signoff.taskId !== null && keyByTask.get(signoff.taskId) === providerKey,
      )
      .map((signoff) => signoff.signedBy),
  )
  // Whatever else the server adds to the preparer list, a bound task signer
  // who is the certifier is refused.
  if (bound.has(userId)) return 'preparer'
  if (!TASK_BOUND_ONLY.includes(reconType)) return 'unknown'
  if (bound.size > 0) return 'allowed'

  const signers = new Set(taskSignoffs.map((signoff) => signoff.signedBy))
  if (signers.size === 0) return 'no_preparer'
  if (signers.size === 1 && signers.has(userId)) return 'sole_preparer'
  return 'allowed'
}

export function isExcluded(verdict: CertifierVerdict): boolean {
  return verdict === 'preparer' || verdict === 'sole_preparer' || verdict === 'no_preparer'
}

/** Whether the run already carries a certification of this type (the server answers 409). */
export function isCertifiedOnRun(run: { signoffs: readonly CloseSignoffDTO[] }, reconType: CloseReconType): boolean {
  return run.signoffs.some((signoff) => signoff.kind === 'reconciliation' && signoff.reconType === reconType)
}

/**
 * What the run's own readiness task last said about this reconciliation:
 * `false` when it disagrees (the server refuses with ERR_CLOSE_NOT_READY),
 * `true` when it agrees, `null` when no task on the run carries the answer.
 */
export function runReadinessFor(
  run: { tasks: readonly Pick<CloseTaskDTO, 'readinessKey' | 'taskKind' | 'computedReady'>[] },
  reconType: CloseReconType,
): boolean | null {
  const task = run.tasks.find(
    (candidate) => candidate.taskKind === 'readiness' && candidate.readinessKey === RECON_PROVIDER_KEYS[reconType],
  )
  if (task === undefined || task.computedReady === undefined || task.computedReady === null) return null
  return task.computedReady
}

/** Why a reconciliation type cannot be certified on this run by this person. */
export type ReconUnavailableReason =
  | 'certified'
  | 'preparer'
  | 'sole_preparer'
  | 'no_preparer'
  | 'not_reconciled'
  | 'not_entitled'

export interface ReconAvailability {
  reconType: CloseReconType
  available: boolean
  reason: ReconUnavailableReason | null
}

/** A sub-ledger's current answer, from the reconciliation report. */
export interface ReconAnswer {
  ready: boolean
  skipped?: boolean
  error?: string
}

/**
 * Every type, with whether this person may certify it on this run — each
 * "no" is a refusal the server is certain to give: already certified (409),
 * a preparer (403 ERR_SOD_VIOLATION), the sub-ledger disagrees or could not be
 * compared (422 ERR_CLOSE_NOT_READY), or its module is not in the plan (422).
 *
 * `answers` is the reconciliation report's per-type answer when the caller has
 * one; otherwise the run's own readiness task is consulted, and with neither
 * the type stays available for the server to judge.
 */
export function reconAvailability(
  run: {
    signoffs: readonly CloseSignoffDTO[]
    tasks: readonly Pick<CloseTaskDTO, 'id' | 'readinessKey' | 'taskKind' | 'computedReady'>[]
  },
  userId: number | null,
  answers: Partial<Record<CloseReconType, ReconAnswer>> = {},
): ReconAvailability[] {
  return RECON_TYPES.map((reconType) => {
    const no = (reason: ReconUnavailableReason): ReconAvailability => ({ reconType, available: false, reason })
    if (isCertifiedOnRun(run, reconType)) return no('certified')
    const verdict = certifierVerdict(run, reconType, userId)
    if (verdict === 'preparer' || verdict === 'sole_preparer' || verdict === 'no_preparer') return no(verdict)
    const answer = answers[reconType]
    if (answer !== undefined) {
      if (answer.skipped === true) return no('not_entitled')
      if (!answer.ready || (answer.error !== undefined && answer.error !== '')) return no('not_reconciled')
    } else if (runReadinessFor(run, reconType) === false) {
      return no('not_reconciled')
    }
    return { reconType, available: true, reason: null }
  })
}

/* ------------------------------------------------------------------ writes */

/**
 * Upload the evidence file onto the close run it evidences. Multipart through
 * the session-guarded client; the server sniffs and stores it, and its key is
 * built server-side. One Idempotency-Key per submission, reused on retry.
 */
export function uploadReconciliationEvidence(runId: number, file: File, idempotencyKey: string): Promise<AttachmentDTO> {
  return uploadAttachment({ module: 'close', entity: 'close.run', entityId: runId }, file, idempotencyKey)
}

export function certifyReconciliation(
  runId: number,
  body: { reconType: CloseReconType; evidenceAttachmentId: number; note: string },
  idempotencyKey: string,
): Promise<CloseCertificationDTO> {
  const note = body.note.trim()
  return closeRunsCertifyReconciliation(
    api(),
    { id: String(runId) },
    {
      reconType: body.reconType,
      evidenceAttachmentId: body.evidenceAttachmentId,
      ...(note === '' ? {} : { note }),
    },
    idempotencyKey,
  )
}

/** Codes the certification refuses with, each explained in the dialog's own words. */
export const CERTIFY_ERROR_CODES = {
  sod: 'ERR_SOD_VIOLATION',
  notReady: 'ERR_CLOSE_NOT_READY',
  already: 'ERR_CLOSE_RECON_ALREADY_CERTIFIED',
  validation: 'ERR_VALIDATION',
  locked: 'ERR_RECORD_LOCKED',
} as const
